import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { DatePipe, LowerCasePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { take } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { CarDetailContactCardsComponent } from './car-detail-contact-cards/car-detail-contact-cards.component';
import { CarDetailElectroStatsComponent } from './car-detail-electro-stats/car-detail-electro-stats.component';
import { CarDetailEquipmentComponent } from './car-detail-equipment/car-detail-equipment.component';
import { CarDetailGalleryComponent } from './car-detail-gallery/car-detail-gallery.component';
import { CarDetailHeaderComponent } from './car-detail-header/car-detail-header.component';
import { CarDetailHeaderLeftComponent } from './car-detail-header/car-detail-header-left/car-detail-header-left.component';
import { CarDetailHeaderRightComponent } from './car-detail-header/car-detail-header-right/car-detail-header-right.component';
import { CarDetailHeaderTitleComponent } from './car-detail-header/car-detail-header-title/car-detail-header-title.component';
import { CarDetailInfoBoxesComponent } from './car-detail-info-boxes/car-detail-info-boxes.component';
import { CarDetailListComponent } from './car-detail-list/car-detail-list.component';
import { CarDetailListItemComponent } from './car-detail-list-item/car-detail-list-item.component';
import { CarDetailParamsComponent } from './car-detail-params/car-detail-params.component';
import { CarDetailPriceCompareComponent } from './car-detail-price-compare/car-detail-price-compare.component';
import { ButtonComponent } from '@atoms/button/button.component';
import { ContentComponent } from '@atoms/content/content.component';
import { IconBoxComponent } from '@atoms/icon-box/icon-box.component';
import { MainComponent } from '@atoms/main/main.component';
import { MapComponent } from '@atoms/map/map.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { SectionComponent } from '@atoms/section/section.component';
import { TitleComponent } from '@atoms/title/title.component';
import { BottomBarComponent } from '@molecules/bottom-bar/bottom-bar.component';
import { BranchPromoComponent } from '@molecules/branch-promo/branch-promo.component';
import { BranchPromoContentComponent } from '@molecules/branch-promo/branch-promo-content/branch-promo-content.component';
import { BranchPromoIntroComponent } from '@molecules/branch-promo/branch-promo-intro/branch-promo-intro.component';
import { BranchPromoTitleComponent } from '@molecules/branch-promo/branch-promo-title/branch-promo-title.component';
import { FinancingCalculatorComponent } from '@organisms/financing-calculator/financing-calculator.component';
import { ContactBarComponent } from '@organisms/contact-bar/contact-bar.component';
import { RecommenderComponent } from '@organisms/recommender/recommender.component';
import { ModalDirective } from '@directives/modal.directive';
import { GtmService } from '@services/gtm.service';
import { ModalService } from '@services/modal.service';
import { ScrolltoService } from '@services/scrollto.service';
import { SeoService } from '@services/seo.service';
import { PhonePipe } from '@pipes/phone.pipe';
import { ThousandsPipe } from '@pipes/thousands.pipe';
import { CarItem } from '@models/car.model';
import { FinancingFormService } from '@services/financing-form.service';
import { CarValuationService } from '@services/car-valuation.service';
import { OnlineFormService } from '@services/online-form.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-car-detail',
  imports: [
    DatePipe,
    LowerCasePipe,
    TranslateModule,
    CarDetailContactCardsComponent,
    CarDetailElectroStatsComponent,
    CarDetailEquipmentComponent,
    CarDetailGalleryComponent,
    CarDetailHeaderComponent,
    CarDetailHeaderLeftComponent,
    CarDetailHeaderRightComponent,
    CarDetailHeaderTitleComponent,
    CarDetailInfoBoxesComponent,
    CarDetailListComponent,
    CarDetailListItemComponent,
    CarDetailParamsComponent,
    CarDetailPriceCompareComponent,
    ButtonComponent,
    ContentComponent,
    IconBoxComponent,
    MainComponent,
    MapComponent,
    ParagraphComponent,
    SectionComponent,
    TitleComponent,
    BottomBarComponent,
    BranchPromoComponent,
    BranchPromoContentComponent,
    BranchPromoIntroComponent,
    BranchPromoTitleComponent,
    FinancingCalculatorComponent,
    ContactBarComponent,
    RecommenderComponent,
    ModalDirective,
    PhonePipe,
    ThousandsPipe,
  ],
  templateUrl: './car-detail.component.html',
  styleUrl: './car-detail.component.scss',
})
export class CarDetailComponent implements OnInit {
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private cd = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  public globals = inject(Globals);
  private modalService = inject(ModalService);
  private gtmService = inject(GtmService);
  private scrolltoService = inject(ScrolltoService);
  private seoService = inject(SeoService);
  private thousandsPipe = inject(ThousandsPipe);
  private financingFormService = inject(FinancingFormService);
  private carValuationService = inject(CarValuationService);
  private onlineFormService = inject(OnlineFormService);
  public car?: CarItem;
  public bottomBarSticky: boolean = true;

  @HostListener('window:scroll', ['$event']) onScroll(): void {
    const bottomBarEl = document.querySelector('.car-detail__bottom-bar');
    if (!bottomBarEl) return;
    const bottomBarHeight = bottomBarEl.getBoundingClientRect().height;
    const bottomBarOffset = bottomBarEl.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;
    if (bottomBarOffset - windowHeight + bottomBarHeight <= 0) {
      this.bottomBarSticky = false;
    } else {
      this.bottomBarSticky = true;
    }
  }

  openCarDemandModal(): void {
    this.modalService.open('<app-car-demand-modal></app-car-demand-modal>', 'medium');
  }

  gtm(id?: string): void {
    if (!id) return;
    this.gtmService.pushGtmPageType({ event: id });
  }

  setTitleAndDescription(carMakeModel: string, carInfo: string): void {
    let seoTitle: string = this.translateService.instant('carDetail.seoTitle', { title: carMakeModel + ' ' + carInfo });
    let description: string = this.translateService.instant('carDetail.seoDescription', { title: carMakeModel + ' ' + carInfo });
    if (this.translateService.instant('carDetail.seoTitle') === 'carDetail.seoTitle') {
      seoTitle = `${carMakeModel} ${carInfo} na prodej`;
      description = `${carMakeModel} ${carInfo} na prodej. Největší výběr předváděcích vozů s nízkým nájezdem a do 5 let stáří | Mototechna`;
    }
    this.seoService.setTitle(seoTitle);
    this.seoService.setDescription(description);
    if (this.car) this.seoService.setOgData({ type: 'article', title: seoTitle, description: description, image: this.car.gallery?.[0]?.data.src });
  }

  ngOnInit(): void {
    this.document.body.classList.add('smartsupp-position');

    this.financingFormService.resetData();
    this.carValuationService.resetForm();
    this.onlineFormService.resetForm();

    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
      this.scrolltoService.scrollTo('top', 'instant');
      this.car = response['data']['car'];
      const makeParam = this.activatedRoute.snapshot.paramMap.get('make');
      const modelParam = this.activatedRoute.snapshot.paramMap.get('model');

      if (!this.car) {
        if (makeParam && modelParam) {
          this.router.navigate([this.globals.routes()['list'].url, makeParam, modelParam]);
        } else {
          this.router.navigate([this.globals.routes()['notFound'].url]);
        }
        return;
      }

      if (makeParam !== this.car?.make.slug || modelParam !== this.car?.model.slug) {
        const url = this.globals.routes()['detail'].url;
        this.router.navigate([url, this.car?.make.slug, this.car?.model.slug, this.car?.id]);
      }

      const dataLayer = {
        productId: this.car.aaaid,
        pageCategory: this.car.price.cash,
        palivo: this.car.fuel?.title,
        karoserie: this.car.body?.title,
        znacka: this.car.make.title,
        model: this.car.model.title,
        prevodovka: this.car.gearbox?.title,
        barva: this.car.body?.externalColor.title,
        rokVyroby: this.car.productionYear,
        najeto: this.car.mileage,
        motorizace: this.car.engine?.title + ' ' + this.car.engine?.performanceKW + 'kW' + (this.car.engine?.is4x4 ? ' 4x4' : ''),
      };
      this.gtmService.pushGtmDataPage(dataLayer);

      const carMakeModel: string = this.car.make.title + ' ' + this.car.model.title;
      const carInfo: string = this.car.productionYear + ', ' + this.car.engine?.title + ', ' + this.thousandsPipe.transform(this.car.mileage, 'breaking') + ' ' + this.car.mileageUnit.toLowerCase() + (this.car.engine?.is4x4 ? ' 4x4' : '') + ' ' + this.car.body?.title?.trim() + ', ' + this.car.gearbox?.title + ', ' + this.car.fuel?.title;
      this.setTitleAndDescription(carMakeModel, carInfo);
      this.translateService.onLangChange.pipe(take(1)).subscribe(() => {
        this.setTitleAndDescription(carMakeModel, carInfo);
      });
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.document.body.classList.remove('smartsupp-position');
  }
}
